import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { useRouterActions } from 'react-resource-router';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ButtonGroup } from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import { Checkbox } from '@atlaskit/checkbox';
import { useFlags } from '@atlaskit/flag';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import SuccessIcon from '@atlaskit/icon/core/migration/success--check-circle';
import { G300, R400 } from '@atlaskit/theme/colors';
import { useFormSaveErrorsActions } from '@atlassian/proforma-common-core/form-system';
import { convertAnswers } from '@atlassian/proforma-common-core/form-system-utils';
import { getFormSaveErrors } from '@atlassian/proforma-common-core/jira-common-models';

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useIssueCreateService } from '../../../services/issue-create';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths

import { copyToClipboard } from '../../utils/copy-to-clipboard';
import { getFormValidationResultMessage } from '../../utils/process-form-load-error';
import { messages } from './messages';
import { IssueFormsFooter, IssueFormsFooterLeftWrapper, IssueFormsFooterWrapper } from './styled';
export const Footer = observer(({
  formDetails,
  formStore,
  project,
  showCreateAnother,
  userIsProjectAdmin,
  onIssueCreated
}) => {
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  const {
    showFlag
  } = useFlags();
  const intl = useIntl();
  const [{
    data: issueCreateResponse,
    error: issueCreateError,
    loading: issueCreating
  }, {
    createIssue,
    reset: resetCreateIssue
  }] = useIssueCreateService();
  const {
    push
  } = useRouterActions();
  const {
    set: setFormSaveErrors,
    clear: clearFormSaveErrors
  } = useFormSaveErrorsActions();
  const [createAnother, setCreateAnother] = useState(false);
  const [creatingIssue, setCreatingIssue] = useState(false);
  useEffect(() => {
    if (issueCreateResponse) {
      const issueUrl = `/browse/${issueCreateResponse.issueKey}`;
      if (onIssueCreated) {
        onIssueCreated(issueCreateResponse.issueKey);
      } else if (createAnother) {
        showFlag({
          actions: [{
            content: /*#__PURE__*/React.createElement(FormattedMessage, messages.successfullyCreatedFlagViewIssueAction),
            href: issueUrl,
            target: '_blank'
          }, {
            content: /*#__PURE__*/React.createElement(FormattedMessage, messages.successfullyCreatedFlagCopyLinkAction),
            onClick: () => copyToClipboard(`${window.location.origin}${issueUrl}`)
          }],
          icon: /*#__PURE__*/React.createElement(SuccessIcon, {
            spacing: "spacious",
            label: "Success",
            color: `var(--ds-icon-success, ${G300})`
          }),
          id: 'create-success',
          title: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.successfullyCreatedFlagTitle, {
            values: {
              issueKey: issueCreateResponse.issueKey
            }
          }))
        });
      } else {
        push(issueUrl);
      }
      resetCreateIssue();
      clearFormSaveErrors();
      // Reset `formStore`:
      formStore.discardModifiedAnswers();
      formStore.hideValidations();
      setCreatingIssue(false);
    }
  }, [clearFormSaveErrors, createAnother, formStore, issueCreateResponse, onIssueCreated, push, resetCreateIssue, showFlag]);
  useEffect(() => {
    if (issueCreateError) {
      setCreatingIssue(false);
      const newFormValidationResultMessage = getFormValidationResultMessage(issueCreateError, project, formDetails, intl, push, showFlag, createAnalyticsEvent, userIsProjectAdmin, false);
      const formSaveErrors = getFormSaveErrors(issueCreateError);
      if (newFormValidationResultMessage) {
        const {
          title,
          description,
          actions
        } = newFormValidationResultMessage;
        showFlag({
          id: 'save-conflict',
          icon: /*#__PURE__*/React.createElement(ErrorIcon, {
            spacing: "spacious",
            label: "error",
            color: `var(--ds-icon-danger, ${R400})`
          }),
          title,
          description,
          actions: actions.map(action => ({
            ...action,
            target: '_blank'
          }))
        });
      } else if (formSaveErrors) {
        const showGeneralErrorMsg = Object.keys(formSaveErrors.errorsByQuestionId).length === 0 && formSaveErrors.generalErrors.length > 0;
        setFormSaveErrors(formSaveErrors.errorsByQuestionId);
        showFlag({
          icon: /*#__PURE__*/React.createElement(ErrorIcon, {
            spacing: "spacious",
            label: "error",
            color: `var(--ds-icon-danger, ${R400})`
          }),
          id: 'create-error',
          description: showGeneralErrorMsg ? formSaveErrors.generalErrors[0] : /*#__PURE__*/React.createElement(FormattedMessage, messages.failedCreatingWithSaveErrorsFlagDescription),
          title: /*#__PURE__*/React.createElement(FormattedMessage, messages.failedCreatingFlagTitle)
        });
      } else {
        showFlag({
          icon: /*#__PURE__*/React.createElement(ErrorIcon, {
            spacing: "spacious",
            label: "error",
            color: `var(--ds-icon-danger, ${R400})`
          }),
          id: 'create-error',
          description: /*#__PURE__*/React.createElement(FormattedMessage, messages.failedCreatingFlagDescription),
          title: /*#__PURE__*/React.createElement(FormattedMessage, messages.failedCreatingFlagTitle)
        });
      }
      resetCreateIssue();
    }
    // NOTE: `intl` from `useIntl()` must not be used in a dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAnalyticsEvent, formDetails, issueCreateError, project, push, resetCreateIssue, showFlag, userIsProjectAdmin]);
  const onCreateClick = () => {
    if (!creatingIssue && !issueCreating) {
      setCreatingIssue(true);
      if (formStore.validateOnCreate) {
        formStore.validate();
      }
      if (formStore.invalid) {
        setCreatingIssue(false);
      } else {
        createIssue(formDetails, convertAnswers(formStore.questions));
      }
    }
  };
  return /*#__PURE__*/React.createElement(IssueFormsFooterWrapper, null, /*#__PURE__*/React.createElement(IssueFormsFooter, null, /*#__PURE__*/React.createElement(IssueFormsFooterLeftWrapper, null, showCreateAnother && /*#__PURE__*/React.createElement(Checkbox, {
    isChecked: createAnother,
    label: /*#__PURE__*/React.createElement(FormattedMessage, messages.createAnotherLabel),
    onChange: event => setCreateAnother(event.target.checked),
    testId: "proforma-jira-issue-forms.ui.issue-forms.footer.create-another-checkbox"
  })), /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(LoadingButton, {
    appearance: "primary",
    isLoading: creatingIssue || issueCreating,
    onClick: onCreateClick,
    testId: "proforma-jira-issue-forms.ui.issue-forms.footer.create-button",
    isDisabled: formStore.isUploadingMedia
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.createButtonLabel)))));
});